import type { ArtworkMode } from "./artwork-controller";
import type { PvcConfiguration } from "./pricing-controller";

/* ============================================================
   Submission + server contracts
   ============================================================ */

export type PvcOrderSubmission = {
  configuration: PvcConfiguration;
  schemaVersion: number;
  artwork: {
    artworkMode: ArtworkMode;
    artworkFileId: string | null;
  };
  quote: {
    configurationHash: string;
    pricingVersion: string;
  };
};

export type CartSummary = {
  id: string;
  totalQuantity: number;
  totalWithTax: number;
  currencyCode: string;
};

export type CartSnapshot = CartSummary & {
  addedOrUpdatedLine: {
    id: string;
    quantity: number;
    productName: string;
    totalWithTax: number;
  };
};

export type OrderLineSuccess = {
  clientMutationId: string;
  orderLineId: string;
  orderLineVersion: number;
  cart: CartSnapshot;
};

export type ServerFieldError = {
  field: string;
  code: string;
};

export type OrderMutationConflict =
  | { code: "CONFIGURATION_INVALID"; fieldErrors: ServerFieldError[] }
  | { code: "PRODUCT_SCHEMA_CHANGED"; currentSchemaVersion: number }
  | { code: "OPTION_UNAVAILABLE"; invalidOptionIds: string[] }
  | { code: "ARTWORK_FILE_INVALID" }
  | { code: "DATE_UNAVAILABLE"; earliestAvailableDate: string | null }
  | { code: "PRICE_CHANGED" }
  | { code: "CART_SESSION_EXPIRED" }
  | { code: "CART_OWNERSHIP_CONFLICT"; guestCart: CartSummary; accountCart: CartSummary }
  | { code: "ORDER_LINE_VERSION_CONFLICT"; currentVersion: number };

export type GatewayMutationResult =
  | {
      type: "success";
      clientMutationId: string;
      result: Omit<OrderLineSuccess, "clientMutationId">;
    }
  | {
      type: "conflict";
      clientMutationId: string;
      conflict: OrderMutationConflict;
    };

export type MutationResolution =
  | {
      status: "committed";
      clientMutationId: string;
      result: Omit<OrderLineSuccess, "clientMutationId">;
    }
  | { status: "pending"; clientMutationId: string }
  | { status: "not-found"; clientMutationId: string };

export interface OrderLineGateway {
  addPvcSign(input: PvcOrderSubmission & { clientMutationId: string }): Promise<GatewayMutationResult>;

  updatePvcOrderLine(
    input: PvcOrderSubmission & {
      clientMutationId: string;
      orderLineId: string;
      expectedVersion: number;
    }
  ): Promise<GatewayMutationResult>;

  resolveMutation(clientMutationId: string): Promise<MutationResolution>;

  recoverCartSession(): Promise<void>;

  resolveCartOwnership(choice: "guest-cart" | "account-cart"): Promise<void>;
}

export interface MutationIdFactory {
  create(): string;
}

/* ============================================================
   Failure + outcome contracts
   ============================================================ */

export type OrderMutationFailureCode =
  | "NETWORK"
  | "OFFLINE"
  | "RESOLUTION_FAILED"
  | "CART_SESSION_RECOVERY_FAILED"
  | "CART_OWNERSHIP_RESOLUTION_FAILED"
  | "UNKNOWN";

export type OrderMutationFailure = {
  code: OrderMutationFailureCode;
  cause?: unknown;
};

/**
 * Gateway adapters should throw this when they know whether a failed request
 * could have committed on the server. Unknown is deliberately conservative:
 * it prevents an unsafe automatic retry.
 */
export class OrderTransportError extends Error {
  constructor(
    public readonly outcome: "definite-failure" | "unknown",
    public readonly failure: OrderMutationFailure,
    message = failure.code
  ) {
    super(message);
    this.name = "OrderTransportError";
  }
}

export type OrderMutationOutcome =
  | { type: "success"; result: OrderLineSuccess }
  | { type: "conflict"; conflict: OrderMutationConflict }
  | { type: "unknown-outcome"; clientMutationId: string }
  | { type: "pending"; clientMutationId: string }
  | { type: "not-found"; clientMutationId: string }
  | { type: "failed"; failure: OrderMutationFailure }
  | {
      type: "discarded";
      reason:
        | "disposed"
        | "superseded"
        | "operation-in-progress"
        | "resolution-required"
        | "no-unknown-operation";
    };

export type CartSessionRecoveryOutcome =
  | { type: "recovered" }
  | { type: "failed"; failure: OrderMutationFailure }
  | { type: "discarded"; reason: "disposed" | "operation-in-progress" };

export type CartOwnershipResolutionOutcome =
  | { type: "resolved"; choice: "guest-cart" | "account-cart" }
  | { type: "failed"; failure: OrderMutationFailure }
  | { type: "discarded"; reason: "disposed" | "not-in-conflict" | "operation-in-progress" };

/* ============================================================
   Controller state
   ============================================================ */

export type OrderLineState =
  | { status: "idle" }
  | {
      status: "submitting";
      operation: "add" | "update";
      clientMutationId: string;
    }
  | {
      status: "success";
      operation: "add" | "update";
      result: OrderLineSuccess;
    }
  | {
      status: "conflict";
      operation: "add" | "update";
      conflict: OrderMutationConflict;
      resolutionFailure?: OrderMutationFailure;
    }
  | {
      status: "unknown-outcome";
      operation: "add" | "update";
      clientMutationId: string;
      canRetry: boolean;
      lastResolutionFailure?: OrderMutationFailure;
    }
  | {
      status: "recovering";
      operation: "add" | "update";
      clientMutationId: string;
    }
  | { status: "recovering-cart-session" }
  | {
      status: "resolving-cart-ownership";
      choice: "guest-cart" | "account-cart";
    }
  | { status: "error"; failure: OrderMutationFailure };

type StoredOperation =
  | {
      type: "add";
      submission: PvcOrderSubmission;
      clientMutationId: string;
    }
  | {
      type: "update";
      submission: PvcOrderSubmission;
      edit: { orderLineId: string; expectedVersion: number };
      clientMutationId: string;
    };

export interface OrderLineController {
  getState(): OrderLineState;

  add(submission: PvcOrderSubmission): Promise<OrderMutationOutcome>;

  update(
    submission: PvcOrderSubmission,
    edit: { orderLineId: string; expectedVersion: number }
  ): Promise<OrderMutationOutcome>;

  resolveUnknownOutcome(): Promise<OrderMutationOutcome>;
  retryUnknownOutcome(): Promise<OrderMutationOutcome>;

  recoverCartSession(): Promise<CartSessionRecoveryOutcome>;

  resolveCartOwnership(
    choice: "guest-cart" | "account-cart"
  ): Promise<CartOwnershipResolutionOutcome>;

  subscribe(listener: (state: OrderLineState) => void): () => void;
  dispose(): void;
}

/* ============================================================
   Controller
   ============================================================ */

function cloneSubmission(submission: PvcOrderSubmission): PvcOrderSubmission {
  return {
    ...submission,
    configuration: {
      ...submission.configuration,
      addonIds: [...submission.configuration.addonIds],
    },
    artwork: { ...submission.artwork },
    quote: { ...submission.quote },
  };
}

function classifyThrown(error: unknown): {
  unknownOutcome: boolean;
  failure: OrderMutationFailure;
} {
  if (error instanceof OrderTransportError) {
    return {
      unknownOutcome: error.outcome === "unknown",
      failure: error.failure,
    };
  }

  // Conservative default: an arbitrary transport exception may have happened
  // after the server committed. Never turn that uncertainty into a duplicate.
  return {
    unknownOutcome: true,
    failure: { code: "NETWORK", cause: error },
  };
}

export function createOrderLineController(options: {
  gateway: OrderLineGateway;
  mutationIds: MutationIdFactory;
}): OrderLineController {
  const { gateway, mutationIds } = options;

  let state: OrderLineState = { status: "idle" };
  let disposed = false;
  let operationRevision = 0;
  let uncertainOperation: StoredOperation | null = null;
  let resolutionInFlight = false;
  const listeners = new Set<(state: OrderLineState) => void>();

  function emit(next: OrderLineState): void {
    if (disposed) return;
    state = next;
    for (const listener of listeners) listener(state);
  }

  function isOperationBlocked(): boolean {
    return (
      state.status === "submitting" ||
      state.status === "recovering" ||
      state.status === "unknown-outcome" ||
      state.status === "recovering-cart-session" ||
      state.status === "resolving-cart-ownership"
    );
  }

  function successFrom(
    clientMutationId: string,
    result: Omit<OrderLineSuccess, "clientMutationId">
  ): OrderLineSuccess {
    return { clientMutationId, ...result };
  }

  async function executeStoredOperation(
    operation: StoredOperation,
    options: { bypassBusyGuard: boolean }
  ): Promise<OrderMutationOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (!options.bypassBusyGuard && isOperationBlocked()) {
      return { type: "discarded", reason: "operation-in-progress" };
    }

    const revision = ++operationRevision;
    const kind = operation.type;

    emit({
      status: "submitting",
      operation: kind,
      clientMutationId: operation.clientMutationId,
    });

    let result: GatewayMutationResult;
    try {
      result = operation.type === "add"
        ? await gateway.addPvcSign({
            ...cloneSubmission(operation.submission),
            clientMutationId: operation.clientMutationId,
          })
        : await gateway.updatePvcOrderLine({
            ...cloneSubmission(operation.submission),
            clientMutationId: operation.clientMutationId,
            orderLineId: operation.edit.orderLineId,
            expectedVersion: operation.edit.expectedVersion,
          });
    } catch (error) {
      if (disposed) return { type: "discarded", reason: "disposed" };
      if (revision !== operationRevision) {
        return { type: "discarded", reason: "superseded" };
      }

      const classified = classifyThrown(error);
      if (classified.unknownOutcome) {
        uncertainOperation = operation;
        emit({
          status: "unknown-outcome",
          operation: kind,
          clientMutationId: operation.clientMutationId,
          canRetry: false,
          lastResolutionFailure: classified.failure,
        });
        return { type: "unknown-outcome", clientMutationId: operation.clientMutationId };
      }

      uncertainOperation = null;
      emit({ status: "error", failure: classified.failure });
      return { type: "failed", failure: classified.failure };
    }

    if (disposed) return { type: "discarded", reason: "disposed" };
    if (revision !== operationRevision) {
      return { type: "discarded", reason: "superseded" };
    }

    // Echo validation: if the gateway gives us a response for a different
    // mutation id, the intended request outcome is unknown. Do not leave the
    // controller stuck in "submitting", and never retry with a fresh id.
    if (result.clientMutationId !== operation.clientMutationId) {
      uncertainOperation = operation;
      emit({
        status: "unknown-outcome",
        operation: kind,
        clientMutationId: operation.clientMutationId,
        canRetry: false,
        lastResolutionFailure: { code: "UNKNOWN" },
      });
      return { type: "unknown-outcome", clientMutationId: operation.clientMutationId };
    }

    uncertainOperation = null;

    if (result.type === "conflict") {
      emit({ status: "conflict", operation: kind, conflict: result.conflict });
      return { type: "conflict", conflict: result.conflict };
    }

    const success = successFrom(operation.clientMutationId, result.result);
    emit({ status: "success", operation: kind, result: success });
    return { type: "success", result: success };
  }

  async function add(submission: PvcOrderSubmission): Promise<OrderMutationOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (isOperationBlocked()) return { type: "discarded", reason: "operation-in-progress" };

    const operation: StoredOperation = {
      type: "add",
      submission: cloneSubmission(submission),
      clientMutationId: mutationIds.create(),
    };
    return executeStoredOperation(operation, { bypassBusyGuard: true });
  }

  async function update(
    submission: PvcOrderSubmission,
    edit: { orderLineId: string; expectedVersion: number }
  ): Promise<OrderMutationOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (isOperationBlocked()) return { type: "discarded", reason: "operation-in-progress" };

    const operation: StoredOperation = {
      type: "update",
      submission: cloneSubmission(submission),
      edit: { ...edit },
      clientMutationId: mutationIds.create(),
    };
    return executeStoredOperation(operation, { bypassBusyGuard: true });
  }

  async function resolveUnknownOutcome(): Promise<OrderMutationOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (!uncertainOperation) return { type: "discarded", reason: "no-unknown-operation" };
    if (resolutionInFlight || state.status === "submitting") {
      return { type: "discarded", reason: "operation-in-progress" };
    }

    const operation = uncertainOperation;
    const revision = ++operationRevision;
    resolutionInFlight = true;
    emit({
      status: "recovering",
      operation: operation.type,
      clientMutationId: operation.clientMutationId,
    });

    let resolution: MutationResolution;
    try {
      resolution = await gateway.resolveMutation(operation.clientMutationId);
    } catch (error) {
      resolutionInFlight = false;
      if (disposed) return { type: "discarded", reason: "disposed" };
      if (revision !== operationRevision) {
        return { type: "discarded", reason: "superseded" };
      }
      const failure: OrderMutationFailure = {
        code: "RESOLUTION_FAILED",
        cause: error,
      };
      emit({
        status: "unknown-outcome",
        operation: operation.type,
        clientMutationId: operation.clientMutationId,
        canRetry: false,
        lastResolutionFailure: failure,
      });
      return { type: "failed", failure };
    }

    resolutionInFlight = false;
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (revision !== operationRevision) {
      return { type: "discarded", reason: "superseded" };
    }
    if (resolution.clientMutationId !== operation.clientMutationId) {
      emit({
        status: "unknown-outcome",
        operation: operation.type,
        clientMutationId: operation.clientMutationId,
        canRetry: false,
        lastResolutionFailure: { code: "RESOLUTION_FAILED" },
      });
      return { type: "failed", failure: { code: "RESOLUTION_FAILED" } };
    }

    if (resolution.status === "committed") {
      uncertainOperation = null;
      const success = successFrom(operation.clientMutationId, resolution.result);
      emit({ status: "success", operation: operation.type, result: success });
      return { type: "success", result: success };
    }

    if (resolution.status === "pending") {
      emit({
        status: "recovering",
        operation: operation.type,
        clientMutationId: operation.clientMutationId,
      });
      return { type: "pending", clientMutationId: operation.clientMutationId };
    }

    emit({
      status: "unknown-outcome",
      operation: operation.type,
      clientMutationId: operation.clientMutationId,
      canRetry: true,
    });
    return { type: "not-found", clientMutationId: operation.clientMutationId };
  }

  async function retryUnknownOutcome(): Promise<OrderMutationOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (!uncertainOperation) return { type: "discarded", reason: "no-unknown-operation" };
    if (state.status !== "unknown-outcome" || !state.canRetry) {
      return { type: "discarded", reason: "resolution-required" };
    }

    // Same business operation, same idempotency key. The operation revision is
    // new so any late resolution response from the previous attempt is dropped.
    return executeStoredOperation(uncertainOperation, { bypassBusyGuard: true });
  }

  async function recoverCartSession(): Promise<CartSessionRecoveryOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (isOperationBlocked()) return { type: "discarded", reason: "operation-in-progress" };

    const revision = ++operationRevision;
    emit({ status: "recovering-cart-session" });
    try {
      await gateway.recoverCartSession();
    } catch (error) {
      if (disposed) return { type: "discarded", reason: "disposed" };
      if (revision !== operationRevision) return { type: "discarded", reason: "operation-in-progress" };
      const failure: OrderMutationFailure = {
        code: "CART_SESSION_RECOVERY_FAILED",
        cause: error,
      };
      emit({ status: "error", failure });
      return { type: "failed", failure };
    }

    if (disposed) return { type: "discarded", reason: "disposed" };
    if (revision !== operationRevision) return { type: "discarded", reason: "operation-in-progress" };
    emit({ status: "idle" });
    return { type: "recovered" };
  }

  async function resolveCartOwnership(
    choice: "guest-cart" | "account-cart"
  ): Promise<CartOwnershipResolutionOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };
    if (isOperationBlocked()) return { type: "discarded", reason: "operation-in-progress" };
    if (state.status !== "conflict" || state.conflict.code !== "CART_OWNERSHIP_CONFLICT") {
      return { type: "discarded", reason: "not-in-conflict" };
    }

    const ownershipConflict = state.conflict;
    const ownershipOperation = state.operation;
    const revision = ++operationRevision;
    emit({ status: "resolving-cart-ownership", choice });
    try {
      await gateway.resolveCartOwnership(choice);
    } catch (error) {
      if (disposed) return { type: "discarded", reason: "disposed" };
      if (revision !== operationRevision) return { type: "discarded", reason: "operation-in-progress" };
      const failure: OrderMutationFailure = {
        code: "CART_OWNERSHIP_RESOLUTION_FAILED",
        cause: error,
      };
      // Preserve the user's two-cart choice so the action can be retried.
      emit({
        status: "conflict",
        operation: ownershipOperation,
        conflict: ownershipConflict,
        resolutionFailure: failure,
      });
      return { type: "failed", failure };
    }

    if (disposed) return { type: "discarded", reason: "disposed" };
    if (revision !== operationRevision) return { type: "discarded", reason: "operation-in-progress" };
    emit({ status: "idle" });
    return { type: "resolved", choice };
  }

  return {
    getState: () => state,
    add,
    update,
    resolveUnknownOutcome,
    retryUnknownOutcome,
    recoverCartSession,
    resolveCartOwnership,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      operationRevision++;
      listeners.clear();
    },
  };
}
